import type { ItineraryItem } from '@elara/client-core/models/ItineraryItemModel';
import type { RoomSelection } from '@elara/domain/models/schemas/room-schemas';
import type { WidgetImage } from '@elara/domain/models/schemas/widget-schemas';

import type {
  BaseItineraryBlockHeaderAttributes,
  BaseItineraryBlockProps,
  ItineraryItemAction,
} from './BaseItineraryBlock.js';

import { useState } from 'react';

import {
  formatOccupancy,
  isRateAvailableForMetadata,
  resolveRateFromMetadata,
} from '@elara/client-core/lib/occupancy-utils';
import { pricingInclusionNote } from '@elara/client-core/lib/price-utils';
import { getBedTypeIcon } from '@elara/client-core/lib/room-utils';
import { pluralize } from '@elara/client-core/lib/utils';
import { MEAL_COMBINATIONS } from '@elara/client-core/models/RoomRateModel';
import { bookingDisplayStatusLabel, bookingStatusFlagsOf } from '@elara/domain/booking-status';
import { isAutomatedBookingChannel } from '@elara/domain/models/schemas/itinerary-item-schemas';

import { bookingStatusLabelColor, resolveBookingDisplayStatus } from '#lib/booking-status.js';
import { useRefreshRoomsPricing } from '#lib/useRefreshPricing.js';
import { useItineraryState, useNavigate, useTripsState } from '#states/hooks.js';
import { CoverImage } from '#ui/cards/DetailCardLayout.js';
import { ImageGalleryWidget } from '#ui/chat/ImageGalleryWidget.js';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '#ui/dropdown-menu.js';
import { Button } from '#ui/index.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { PriceStatusBanner } from '#ui/PriceStatusBanner.js';
import { SectionDivider } from '#ui/SectionDivider.js';
import { isBookable, isRoomUnavailable } from '#views/trip/bookings/booking-grouping.js';

import { BaseItineraryBlock } from './BaseItineraryBlock.js';
import { ItemNotesDialog } from './ItemNotesDialog.js';
import { DetailRow } from './ItineraryDetailRow.js';
import { ItineraryDetailSummary } from './ItineraryDetailSummary.js';
import { ItineraryNotesSection } from './ItineraryNotesSection.js';
import { PrintBookingDetails } from './PrintBookingDetails.js';

/**
 * The per-room breakdown shown when a hotel check-in event is expanded: each
 * room the traveler added, with its thumbnail, cost, and booking info (status +
 * confirmation number). Rooms that aren't booked yet can be removed inline.
 */
function HotelRoomList({
  items,
  onShowRoom,
  onRemoveRoom,
  onEditBooking,
  onBookRoom,
  onViewBooking,
}: {
  items: ItineraryItem[];
  onShowRoom: (roomId: string) => void;
  onRemoveRoom: (item: ItineraryItem) => void;
  onEditBooking: (item: ItineraryItem) => void;
  onBookRoom: (item: ItineraryItem) => void;
  onViewBooking: (item: ItineraryItem) => void;
}) {
  const rooms = items.filter((item) => item.roomOption);
  // Externally-booked rooms carry no RoomOption (no Sabre rate/beds/images) — the room label and
  // occupancy come from the confirmation, captured in referenceData/metadata. Render them alongside
  // catalog rooms so a stay booked outside Elara isn't dropped from the expanded card.
  const externalRooms = items.filter((item) => !item.roomOption && item.type === 'room');
  if (rooms.length === 0 && externalRooms.length === 0) return null;

  return (
    <div className="flex flex-col gap-4">
      <SectionDivider label={pluralize(rooms.length + externalRooms.length, 'Room')} />

      {rooms.map((item) => {
        const room = item.roomOption!;
        const metadata = item.metadata as RoomSelection | undefined;
        const rate = item.rate;
        const occupancyLabel = metadata?.occupancy ? formatOccupancy(metadata.occupancy) : undefined;
        const mealName = MEAL_COMBINATIONS.find((combo) => combo.id === rate?.mealCombinationId)?.name;
        // Whether the displayed total already includes taxes/fees (e.g. "Includes taxes and fees").
        const taxNote = rate?.localPricing ? pricingInclusionNote(rate.localPricing) : undefined;
        // Cancellation fact, centralized on the item model (`ItineraryItemModel`) so this row can't
        // drift from what `ViewBookingDialog`/the room guide show for this exact booking.
        const cancellationText = item.cancellationPolicyText;
        const cancellationDeadlineLabel = item.cancellationDeadlineLabel;
        const isFreeCancellation = item.isFreeCancellation;
        // Fold availability into the status so an unavailable room reads "No longer available".
        const displayStatus = resolveBookingDisplayStatus({
          ...bookingStatusFlagsOf(item),
          isUnavailable: isRoomUnavailable(item),
        });
        // Booked/approved rooms are Elara-fulfilled — cancel the booking before removing. A pending
        // cancellation is in limbo (the reservation may still be live), so it can't be removed either.
        const removable = !item.isBooked && !item.isApproved && !item.isCancellationPending;
        // Manual booking entry is for EXTERNAL rooms only (no rate referenceId) — a rate-linked room
        // books exclusively through checkout, and the server rejects manual records on it (ELARA-870).
        // Catalog rooms here always carry a referenceId, so they never offer "Add booking".
        const canEditBooking =
          !item.isCancelled && !isAutomatedBookingChannel(item.booking?.channel) && !item.referenceId;
        // Each RoomOption is its own search, so its freshness banner sits with its room. Only while
        // the room is still live (a settled booking has no price to refresh); self-hides when fresh.
        const showPriceBanner = removable && !item.isCancelled && !isAutomatedBookingChannel(item.booking?.channel);

        return (
          <div key={`${item.id}-${room.id}`} className="flex flex-col gap-3 pb-4 border-b last:border-b-0 last:pb-0">
            <div className="flex items-center gap-3">
              <CoverImage
                imageUrls={room.images?.map((image) => image.url)}
                title={room.images?.[0]?.caption}
                type="room"
                className="w-12 h-12 rounded-sm flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onShowRoom(room.id);
                }}
              />

              <div className="flex-1 min-w-0">
                <button
                  type="button"
                  className="block w-full truncate text-left text-sm font-medium hover:text-accent"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowRoom(room.id);
                  }}
                >
                  {room.displayName}
                </button>
                <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <div>{rate?.displayName}</div>
                  <div>·</div>
                  <div className={`${bookingStatusLabelColor(displayStatus)}`}>
                    {bookingDisplayStatusLabel(displayStatus)}
                  </div>
                </div>
              </div>

              {/* Per-room actions: view booking (when fulfilled) or book (when still bookable). */}
              {item.canViewBooking && (
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewBooking(item);
                  }}
                >
                  <MaterialIcon icon="confirmation_number" />
                  View booking
                </Button>
              )}
              {isBookable(item) && (
                <Button
                  variant="icon"
                  size="icon-sm"
                  tooltip="Book"
                  onClick={(e) => {
                    e.stopPropagation();
                    onBookRoom(item);
                  }}
                >
                  <MaterialIcon icon="shopping_cart_checkout" />
                </Button>
              )}

              {removable && (
                <Button
                  variant="icon"
                  size="icon-sm"
                  tooltip="Remove room"
                  aria-label="Remove room"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveRoom(item);
                  }}
                >
                  <MaterialIcon icon="delete" className="text-destructive" />
                </Button>
              )}

              {/* Only "Add booking" is left for the overflow; Remove is the icon button above. */}
              {canEditBooking && !item.hasBookingInfo && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="icon" size="icon-sm" onClick={(e) => e.stopPropagation()}>
                      <MaterialIcon icon="more_horiz" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setTimeout(() => onEditBooking(item), 50)}>
                      <MaterialIcon icon="add" className="mr-2" size={IconSizes.sm} />
                      Add booking
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* This room's price freshness/availability — bound to its own RoomOption search. */}
            {showPriceBanner && <PriceStatusBanner type="room" model={room} size="sm" />}

            {/* Room attributes as rows: price (with the rate name as its sub-label), occupancy, etc.
                Status is shown under the name above; booking details are via the button in the header. */}
            <div className="flex flex-col text-sm">
              {item.formattedCost && (
                <DetailRow
                  icon="payments"
                  value={
                    <span className={displayStatus === 'unavailable' ? 'line-through opacity-50' : undefined}>
                      {item.formattedCost}
                    </span>
                  }
                  secondary={[item.formattedCostDetail, taxNote].filter(Boolean).join('. ') || undefined}
                />
              )}
              {occupancyLabel && <DetailRow icon="group" value={occupancyLabel} />}
              {rate?.hasMeals && mealName && <DetailRow icon="restaurant" value={`${mealName} included`} />}
              {/* Lead with the resolved free-cancellation deadline (same fact `ViewBookingDialog` shows
                  for this exact booking); the full policy prose is the sub-label. Falls back to a plain
                  Refundable/Non-refundable label pre-booking, when no deadline can resolve yet. */}
              {(rate || cancellationDeadlineLabel || cancellationText) && (
                <DetailRow
                  icon={isFreeCancellation ? 'currency_exchange' : 'money_off'}
                  iconClassName={isFreeCancellation ? 'text-success' : undefined}
                  value={
                    <span className={isFreeCancellation ? 'text-success' : undefined}>
                      {cancellationDeadlineLabel ?? rate?.cancellationPolicyTitle}
                    </span>
                  }
                  secondary={cancellationText}
                />
              )}
              {rate?.loyaltyPoints && rate.loyaltyProgramName && (
                <DetailRow icon="stars_2" value={`Earn ${rate.loyaltyProgramName} points with this stay`} />
              )}
            </div>
          </div>
        );
      })}

      {externalRooms.map((item) => {
        const refData = item.referenceData as { roomName?: unknown } | null | undefined;
        const roomLabel = typeof refData?.roomName === 'string' ? refData.roomName : undefined;
        const metadata = item.metadata as RoomSelection | undefined;
        const occupancyLabel = metadata?.occupancy ? formatOccupancy(metadata.occupancy) : undefined;
        // No Sabre availability to fold in — an external room is never "unavailable", only its
        // booking status (booked/planned/cancelled) matters.
        const displayStatus = resolveBookingDisplayStatus({ ...bookingStatusFlagsOf(item), isUnavailable: false });
        // Manually-recorded bookings can always have their details added/edited (this keeps the
        // overflow menu from being empty once a stay is "booked"). External rooms carry no rate
        // referenceId; a legacy rate-linked stray would be server-rejected anyway (ELARA-870).
        const canEditBooking =
          !item.isCancelled && !isAutomatedBookingChannel(item.booking?.channel) && !item.referenceId;
        // NOTE: removal of an external stay IS supported server-side — DeleteItineraryItemFlow exempts
        // `managedByElara: false` / `channel: 'manual'` bookings from the cancel-first guard. We
        // intentionally HIDE the "Remove room" affordance for now: on a non-Elara stay "Remove" could
        // read as "cancel my reservation" when it only drops our record (the real booking is untouched).
        // To bring it back, reinstate a `removable` flag and the "Remove room" menu item below.

        return (
          <div key={item.id} className="flex flex-col gap-3 pb-4 border-b last:border-b-0 last:pb-0">
            <div className="flex items-center gap-3">
              <CoverImage type="room" className="w-12 h-12 rounded-sm flex-shrink-0" />

              <div className="flex-1 min-w-0">
                <div className="truncate text-sm font-medium">{roomLabel ?? 'Room'}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <div className={`${bookingStatusLabelColor(displayStatus)}`}>
                    {bookingDisplayStatusLabel(displayStatus)}
                  </div>
                </div>
              </div>

              {item.canViewBooking && (
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewBooking(item);
                  }}
                >
                  <MaterialIcon icon="confirmation_number" />
                  View booking
                </Button>
              )}

              {canEditBooking && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="icon" size="icon-sm" onClick={(e) => e.stopPropagation()}>
                      <MaterialIcon icon="more_horiz" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setTimeout(() => onEditBooking(item), 50)}>
                      <MaterialIcon icon={item.hasBookingInfo ? 'edit' : 'add'} className="mr-2" size={IconSizes.sm} />
                      {item.hasBookingInfo ? 'Edit booking' : 'Add booking'}
                    </DropdownMenuItem>
                    {/* "Remove room" intentionally hidden for external stays — see the NOTE above. */}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            <div className="flex flex-col text-sm">
              {item.formattedCost && (
                <DetailRow
                  icon="payments"
                  value={item.formattedCost}
                  secondary={item.formattedCostDetail ?? undefined}
                />
              )}
              {occupancyLabel && <DetailRow icon="group" value={occupancyLabel} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function HotelCheckinBlock({ event, items, size = 'default', className }: BaseItineraryBlockProps) {
  const tripsState = useTripsState();
  const itineraryState = useItineraryState();
  const { navigate } = useNavigate();
  const tripId = tripsState.current?.id;
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false);

  const { canRefresh, isRefreshing, refresh: handleRefresh } = useRefreshRoomsPricing(items);

  if (event.type !== 'hotel_checkin' || items.length === 0 || items.some((i) => i.type !== 'room')) return null;

  const primaryItem = items[0]!;
  const hotel = primaryItem.hotel;
  // Contact/guide/description resolve the catalog hotel first, then fall back to the attached Google
  // Place for an externally-booked stay with no catalog match (see the `stay*`/`guideRef` getters).
  const hotelPhoneNumber = primaryItem.stayPhone;
  const hotelWebsite = primaryItem.stayWebsite;
  const directionsUrl = primaryItem.stayDirectionsUrl;
  const guideRef = primaryItem.guideRef;
  const openGuide = guideRef
    ? () =>
        guideRef.kind === 'hotel' ? tripsState.showHotelGuide(guideRef.id) : tripsState.showPlaceGuide(guideRef.id)
    : undefined;

  // "Book" jumps straight to the hotel checkout. The same route handles one or many
  // rooms, so the footer CTA books every still-bookable room at once while each room row
  // can also be booked on its own.
  const bookableRoomIds = items.filter(isBookable).map((item) => item.id);
  const bookStay = (): void => {
    if (!tripId || bookableRoomIds.length === 0) return;
    navigate(`/checkout/hotel/${tripId}?items=${bookableRoomIds.join(',')}`);
  };
  const bookRoom = (room: ItineraryItem): void => {
    if (!tripId || !room.id) return;
    navigate(`/checkout/hotel/${tripId}?items=${room.id}`);
  };

  // The full-page (lg) block lists address, website and phone as icon rows (`ItineraryDetailSummary`)
  // instead, so they appear in the header and as buttons only on the compact layout.
  const isLarge = size === 'lg';
  const headerAttributes: BaseItineraryBlockHeaderAttributes[] = [];

  if (primaryItem.stayAddress && !isLarge) {
    headerAttributes.push({ icon: 'place', label: primaryItem.stayAddress });
  }

  if (hotelPhoneNumber && !isLarge) {
    headerAttributes.push({ icon: 'call', label: hotelPhoneNumber });
  }

  const title = event.title;

  const actions: ItineraryItemAction[] = [];

  // On the full-page block the guide is the "Read the full guide" row instead of a button.
  if (openGuide && !isLarge) {
    actions.push({
      type: 'primary',
      icon: 'menu_book',
      label: guideRef?.kind === 'place' ? 'Place guide' : 'Hotel guide',
      onClick: openGuide,
    });
  }

  if (hotelWebsite && !isLarge) {
    actions.push({
      type: 'secondary',
      icon: 'link',
      label: 'View website',
      onClick: () => window.open(hotelWebsite, '_blank'),
    });
  }

  if (directionsUrl && !isLarge) {
    actions.push({
      type: 'secondary',
      icon: 'directions',
      label: 'Get directions',
      onClick: () => window.open(directionsUrl, '_blank'),
    });
  }

  if (canRefresh && !isLarge) {
    actions.push({
      type: 'tertiary',
      icon: 'refresh',
      label: isRefreshing ? 'Refreshing...' : 'Refresh pricing',
      onClick: async (e: React.MouseEvent) => handleRefresh(e),
    });
  }

  // Catalog hotel images first; for an unmatched external stay fall back to the attached Google Place's photos.
  const hotelImages = primaryItem.stayImages;
  const roomImages = items.flatMap((item) => item.roomOption?.images ?? []);
  const images = [...hotelImages, ...roomImages].filter((image): image is WidgetImage => image != null);

  // Clicking the block opens a small menu — view the hotel, or jump straight to one of its rooms.
  // Rooms are deduped by RoomOption id (a multi-room booking yields several items with the same room).
  const dedupedRooms = [
    ...new Map(
      items.flatMap((item) => (item.roomOption ? [[item.roomOption.id, item.roomOption] as const] : [])),
    ).values(),
  ];
  const openNavMenu = openGuide ? () => setNavMenuOpen(true) : undefined;
  const wrapWithNavMenu = (block: React.ReactElement): React.ReactElement =>
    openGuide ? (
      <DropdownMenu open={navMenuOpen} onOpenChange={setNavMenuOpen}>
        <div className="relative">
          {/* Invisible anchor so the menu positions over the block; the block's own onClick opens it. */}
          <DropdownMenuTrigger asChild>
            <span aria-hidden className="pointer-events-none absolute inset-0" />
          </DropdownMenuTrigger>
          {block}
        </div>
        <DropdownMenuContent align="start" className="min-w-48">
          <DropdownMenuItem onClick={openGuide}>
            <MaterialIcon icon="villa" className="mr-2" size={IconSizes.sm} />
            {primaryItem.hotelName ?? (guideRef?.kind === 'place' ? 'Place' : 'Hotel')}
          </DropdownMenuItem>
          {dedupedRooms.map((room) => (
            <DropdownMenuItem key={room.id} onClick={() => tripsState.showGuide('room', room.id)}>
              <MaterialIcon icon="bed" className="mr-2" size={IconSizes.sm} />
              {room.displayName}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : (
      block
    );

  const headerBookAction = bookableRoomIds.length > 0 ? { label: `Book`, onClick: bookStay } : undefined;

  if (size === 'sm') {
    return wrapWithNavMenu(
      <BaseItineraryBlock
        event={event}
        items={items}
        title={title}
        images={images}
        canDeleteItem={true}
        headerAttributes={headerAttributes}
        actions={actions}
        headerBookAction={headerBookAction}
        size={size}
        onClick={openNavMenu}
      />,
    );
  }

  if (size === 'default') {
    // Every room gone surfaces "No longer available" as the stay's status (no banner/fade); a
    // partially-unavailable stay keeps its aggregate status, and each room shows its own.
    const allRoomsUnavailable = items.every((item) => {
      const room = item.roomOption;
      if (!room) return false; // Room data not yet loaded — don't treat as unavailable
      return !isRateAvailableForMetadata(room, item.metadata as RoomSelection | undefined);
    });
    // "Sold out" only wins while the stay is still planned — once any room is booked/approved (or
    // the stay is cancelled), that booking status is the truth, not the search option's availability.
    const stayPlanned = !event.bookingStatus || event.bookingStatus === 'planned';

    // Check-in time moves into the leading time chip, "From" above the time on its own line —
    // mirrors the mobile app's hotel row, which shows it there instead of as a header attribute.
    const leadingTime = event.originalFormattedTime ? (
      <span className="flex flex-col">
        <span className="text-2xs">From</span>
        <span className="tabular-nums">{event.originalFormattedTime}</span>
      </span>
    ) : null;

    const headerAttributes: BaseItineraryBlockHeaderAttributes[] = [];
    const nights = primaryItem.roomOption?.nightsCount;
    if (nights) {
      headerAttributes.push({ label: `${nights} ${pluralize(nights, 'night')}` });
    }
    headerAttributes.push({ label: `${items.length} ${pluralize(items.length, 'room')}` });

    // Expanded builder card: booking is handled by the header "Book" button (headerBookAction).
    // Directions/refresh fall into the overflow; per-room removal lives in the room list below.
    const defaultActions: ItineraryItemAction[] = [];

    // The hotel guide is the "View full guide" row in the summary above, so it's not repeated here.
    // Directions live as a link in the summary (below the address) on the expanded card.
    // Refresh pricing isn't repeated here — each room's PriceStatusBanner carries its own recheck.
    // Manual booking entry is per-room (see HotelRoomList) rather than on the stay as a whole.

    return (
      <>
        <BaseItineraryBlock
          event={event}
          items={items}
          title={title}
          images={images}
          canDeleteItem={false}
          actions={defaultActions}
          bookingStatusOverride={allRoomsUnavailable && stayPlanned ? 'unavailable' : undefined}
          headerBookAction={headerBookAction}
          size={size}
          className={className}
          leadingTime={leadingTime}
          headerAttributes={headerAttributes}
          summary={
            hotelImages.length > 0 || primaryItem.stayDescription ? (
              <>
                {hotelImages.length > 0 && (
                  <ImageGalleryWidget images={hotelImages} aspect="video" round="md" maxImages={4} autoplay={false} />
                )}
                {primaryItem.stayDescription && (
                  <p className="text-sm max-w-prose text-pretty line-clamp-3">{primaryItem.stayDescription}</p>
                )}
              </>
            ) : undefined
          }
        >
          <ItineraryDetailSummary
            onShowGuide={openGuide}
            location={primaryItem.stayAddress}
            directionsUrl={directionsUrl}
            website={hotelWebsite}
            phone={hotelPhoneNumber}
          />

          <HotelRoomList
            items={items}
            onShowRoom={(roomId) => tripsState.showGuide('room', roomId)}
            onRemoveRoom={(item) => itineraryState.openDeleteDialog(item)}
            onEditBooking={(item) => itineraryState.openBookingDialog(item)}
            onBookRoom={bookRoom}
            onViewBooking={(item) => itineraryState.openViewBookingDialog(item)}
          />

          <ItineraryNotesSection
            notes={primaryItem.notes}
            advisorNote={primaryItem.booking?.advisorNote}
            onEditNote={() => setIsNotesDialogOpen(true)}
          />
        </BaseItineraryBlock>

        {isNotesDialogOpen && (
          <ItemNotesDialog
            open={isNotesDialogOpen}
            onOpenChange={setIsNotesDialogOpen}
            initialNotes={primaryItem.notes}
            onConfirm={async (notes) => {
              await itineraryState.updateItemNotes(primaryItem.id, notes);
            }}
          />
        )}
      </>
    );
  }

  const hasImages = images.length > 0;

  const removeRoomButton = (item: ItineraryItem) =>
    item.isItemRemovable ? (
      <Button
        variant="icon"
        size="icon-sm"
        tooltip="Remove from itinerary"
        aria-label="Remove from itinerary"
        className="flex-shrink-0 self-start md:self-center print:hidden"
        onClick={(e) => {
          e.stopPropagation();
          itineraryState.openDeleteDialog(item);
        }}
      >
        <MaterialIcon icon="delete" className="text-destructive" />
      </Button>
    ) : null;

  return (
    <>
      <BaseItineraryBlock
        event={event}
        items={items}
        images={images}
        title={primaryItem.hotelName}
        // Each room row carries its own delete button instead of a Remove button per room below.
        canDeleteItem={false}
        headerAttributes={headerAttributes}
        actions={actions}
        className={className}
        size={size}
        onClick={openGuide}
        preHeaderContent={
          event.originalFormattedTime && (
            <>
              <div>·</div>
              <div className="text-xs uppercase tracking-wide">From {event.originalFormattedTime}</div>
            </>
          )
        }
        panelContent={
          hasImages ? (
            <ImageGalleryWidget images={images} aspect="fill" round="none" maxImages={1} autoplay={true} />
          ) : undefined
        }
        panelPosition="left"
      >
        {primaryItem.stayShortDescription && (
          <div className="w-full justify max-w-prose text-pretty">{primaryItem.stayShortDescription}</div>
        )}

        <ItineraryDetailSummary
          location={primaryItem.stayAddress}
          directionsUrl={directionsUrl}
          website={hotelWebsite}
          phone={hotelPhoneNumber}
          onShowGuide={openGuide}
        />

        <div className="flex flex-col border-t overflow-hidden max-w-prose w-full">
          {items.map((item) => {
            const room = item.roomOption;
            const metadata = item.metadata as RoomSelection | undefined;
            const occupancyLabel = metadata?.occupancy ? formatOccupancy(metadata.occupancy) : undefined;

            // External booking (no RoomOption): a lightweight, non-clickable row with the captured
            // room label + occupancy (there's no Sabre rate / bed types / room images to show).
            if (!room) {
              const refData = item.referenceData as { roomName?: unknown } | null | undefined;
              const roomLabel = typeof refData?.roomName === 'string' ? refData.roomName : undefined;
              if (!roomLabel && !occupancyLabel && !item.isItemRemovable) return null;
              return (
                <div key={item.id} className="flex flex-col gap-3 border-b py-4">
                  <div className="flex flex-row items-center gap-4">
                    <CoverImage
                      imageUrls={hotelImages.map((image) => image.url)}
                      type="room"
                      size="lg"
                      className="w-24 h-24 md:w-12 md:h-12 print:w-12 print:h-12 rounded-sm"
                    />
                    <div className="flex-1 flex flex-col">
                      <div className="text-md font-medium">{roomLabel ?? 'Room'}</div>
                      {occupancyLabel && (
                        <div className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
                          <span>{occupancyLabel}</span>
                        </div>
                      )}
                      <PrintBookingDetails item={item} />
                    </div>
                    {removeRoomButton(item)}
                  </div>
                </div>
              );
            }

            const rate = resolveRateFromMetadata(room, metadata);

            return (
              <div
                key={`${item.id}-${room.id}`}
                className="flex flex-col gap-3 border-b py-4 cursor-pointer hover:text-accent"
                onClick={() => tripsState.showGuide('room', room.id)}
              >
                {/* A row on paper too: print width is below `md`, where this stacks on screen. */}
                <div className="flex flex-col md:flex-row md:items-center print:flex-row print:items-center gap-4">
                  <CoverImage
                    imageUrls={room.images?.map((image) => image.url)}
                    title={room.images?.[0]?.caption}
                    type="room"
                    size="lg"
                    className="w-24 h-24 md:w-12 md:h-12 print:w-12 print:h-12 rounded-sm"
                  />

                  <div className="flex-1 flex flex-col">
                    <div className="text-md font-medium">{room.displayName}</div>
                    <div className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
                      {rate && <span>{rate.displayName}</span>}
                      {occupancyLabel && (
                        <>
                          {rate && <span>·</span>}
                          <span>{occupancyLabel}</span>
                        </>
                      )}
                    </div>
                    <PrintBookingDetails item={item} />
                  </div>

                  <div className="text-sm text-muted-foreground flex items-center gap-3">
                    {room.bedTypes.map((bedType) => (
                      <div key={bedType.sabreBedTypeCode ?? bedType.description} className="flex items-center gap-1">
                        <MaterialIcon icon={getBedTypeIcon(bedType.sabreBedTypeCode ?? bedType.code)} />
                        <span>
                          {bedType.count} {pluralize(bedType.count, bedType.description)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {removeRoomButton(item)}
                </div>
              </div>
            );
          })}
        </div>
      </BaseItineraryBlock>

      {isNotesDialogOpen && (
        <ItemNotesDialog
          open={isNotesDialogOpen}
          onOpenChange={setIsNotesDialogOpen}
          initialNotes={primaryItem.notes}
          onConfirm={async (notes) => {
            await itineraryState.updateItemNotes(primaryItem.id, notes);
          }}
        />
      )}
    </>
  );
}
